import React, { useCallback, useEffect, useMemo, useState } from "react";
import UIkit from "uikit";

import StorageService from "../../../../services/storage.service";
import { formatCurrency } from "../data";

const metricConfigs = [
  ["total_inventory_items", "Total Inventory Items", "album"],
  ["total_inventory_value", "Total Inventory Value", "credit-card", "currency"],
  ["low_stock_items", "Low Stock Items", "warning"],
  ["lost_items_this_month", "Lost Items This Month", "minus-circle"],
  ["damaged_items_this_month", "Damaged Items This Month", "bolt"],
  ["transfers_this_month", "Transfers This Month", "move"],
];

const valueFor = (health, key, type) => {
  const value = health?.[key] ?? 0;
  return type === "currency" ? formatCurrency(value) : Number(value || 0).toLocaleString();
};

const SectionRow = ({ title, children }) => (
  <div className="uk-margin-large-bottom">
    <div className="uk-flex uk-flex-between uk-flex-middle">
      <h2 className="uk-margin-remove">{title}</h2>
    </div>
    {children}
  </div>
);

const MetricCard = ({ label, icon, value }) => (
  <div>
    <div className="rbr-card uk-margin-small-right">
      <div className="rbr-card-head">
        <div className="rbr-card-title">
          <span className="rbr-ico" uk-icon={icon}></span>
          {label}
        </div>
      </div>
      <div className="hms-blue-number">{value}</div>
    </div>
  </div>
);

export default function InventoryHealthDashboard() {
  const [health, setHealth] = useState({});
  const [lowStock, setLowStock] = useState([]);

  const loadDashboard = useCallback(async () => {
    try {
      const [healthResp, lowStockResp] = await Promise.all([
        StorageService.inventoryHealth(),
        StorageService.lowStockReport({ page: 0, per: 10 }),
      ]);
      setHealth(healthResp.data || {});
      setLowStock(lowStockResp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load inventory health.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Inventory Health - RueBaRue";
    loadDashboard();
  }, [loadDashboard]);

  const actionRows = useMemo(() => {
    return [...lowStock].sort((a, b) => Number(b.severity || 0) - Number(a.severity || 0));
  }, [lowStock]);

  return (
    <section id="dashboard-template">
      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <h1 className="uk-heading-small uk-margin-remove">Inventory Health</h1>
      </div>

      <div id="hms-main-body">
        <SectionRow title="Inventory">
          <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-3@m uk-margin-small-top" uk-grid="true">
            {metricConfigs.map(([key, label, icon, type]) => (
              <MetricCard
                key={key}
                label={label}
                icon={icon}
                value={valueFor(health, key, type)}
              />
            ))}
          </div>
        </SectionRow>

        <SectionRow title="Action Required">
          {actionRows.length === 0 ? (
            <div className="uk-margin-small-top uk-text-meta">No low stock items need attention.</div>
          ) : (
            <div className="uk-grid-small uk-child-width-1-1 uk-child-width-1-2@m uk-margin-small-top" uk-grid="true">
              {actionRows.map((row) => (
                <div key={`${row.location_id}-${row.item_id}`}>
                  <div className="rbr-card uk-margin-small-right">
                    <div className="rbr-card-head">
                      <div className="rbr-card-title">
                        <span className="rbr-ico" uk-icon="warning"></span>
                        {row.item_name} Below Threshold
                      </div>
                    </div>
                    <div className="uk-text-meta">{row.location_name}</div>
                    <div className="uk-margin-small-top">
                      Current: {row.current_quantity ?? 0}<br />
                      Minimum: {row.minimum_quantity ?? 0}<br />
                      Suggested Reorder: {row.suggested_reorder_quantity ?? "-"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionRow>
      </div>
    </section>
  );
}
